import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import { useDarkMode } from '../darkmode/DarkModeContext'; 
import { useAuth } from '../../pages/auth/AuthContext'; 
import {HomeIcon, CubeIcon, ClipboardDocumentListIcon, CreditCardIcon, UserIcon, ArrowRightOnRectangleIcon, } from "@heroicons/react/24/outline";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeUserDropdown, setActiveUserDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use AuthContext
  const { user, isAuthenticated, loading, logout } = useAuth();
  
  // Use context for dark mode
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Debug log untuk tracking state
  useEffect(() => {
    // console.log('Navbar state update:', { 
    //   user: user ? { email: getUserEmail(), fullName: getUserDisplayName() } : null, 
    //   isAuthenticated, 
    //   loading,
    //   hasValidUserData: hasValidUserData()
  }, [user, isAuthenticated, loading]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Handle logout using AuthContext
  const handleLogout = async () => {
    try {
      // console.log('Starting logout process...');
      
      // Close any open dropdowns
      setActiveUserDropdown(false);
      setIsMenuOpen(false);
      
      // Call logout from AuthContext - this will clear all auth data
      await logout();
      
      // console.log('Logout completed, redirecting...');
      
      // Navigate to home page after logout
      navigate('/', { replace: true });
      
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, still redirect to prevent app issues
      navigate('/', { replace: true });
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setActiveDropdown(null); // Reset dropdown when toggling menu
    setActiveUserDropdown(false); // Reset user dropdown when toggling menu
  };

  const toggleMobileDropdown = (itemName) => {
    setActiveDropdown(activeDropdown === itemName ? null : itemName);
    setActiveUserDropdown(false); // Reset user dropdown when opening other dropdowns
  };

  const toggleUserDropdown = () => {
    setActiveUserDropdown(!activeUserDropdown);
    setActiveDropdown(null); // Reset other dropdowns when opening user dropdown
  };

  // FIXED: Helper function to get actual user data with proper structure handling
  const getActualUserData = () => {
    if (!user) {
      // console.log('getActualUserData: No user');
      return null;
    }
    
    // console.log('getActualUserData raw user:', user);
    
    // Handle nested user structure from sessionStorage: {user: {profile: {actualData}}}
    if (user.user && typeof user.user === 'object') {
      if (user.user.profile && typeof user.user.profile === 'object') {
        // console.log('getActualUserData: Using user.user.profile');
        return user.user.profile;
      }
      // console.log('getActualUserData: Using user.user');
      return user.user;
    }
    
    // Handle direct profile structure: {profile: {actualData}}
    if (user.profile && typeof user.profile === 'object' && !user.email && !user.fullName) {
      // console.log('getActualUserData: Using user.profile');
      return user.profile;
    }
    
    // Handle direct user data
    // console.log('getActualUserData: Using user directly');
    return user;
  };

  // FIXED: Helper function to get user initial
  const getUserInitial = () => {
    const userData = getActualUserData();
    if (!userData) {
      // console.log('getUserInitial: No user data');
      return null;
    }
    
    // console.log('getUserInitial userData:', userData);
    
    // Try fullName first
    if (userData.fullName && typeof userData.fullName === 'string' && userData.fullName.trim()) {
      const initial = userData.fullName.trim().charAt(0).toUpperCase();
      // console.log('getUserInitial from fullName:', initial);
      return initial;
    }
    
    // Try name field
    if (userData.name && typeof userData.name === 'string' && userData.name.trim()) {
      const initial = userData.name.trim().charAt(0).toUpperCase();
      // console.log('getUserInitial from name:', initial);
      return initial;
    }
    
    // Try email as fallback
    if (userData.email && typeof userData.email === 'string' && userData.email.trim()) {
      const initial = userData.email.trim().charAt(0).toUpperCase();
      // console.log('getUserInitial from email:', initial);
      return initial;
    }
    
    // console.log('getUserInitial: No valid data found');
    return null;
  };

  // FIXED: Helper function to get user display name
  const getUserDisplayName = () => {
    const userData = getActualUserData();
    if (!userData) {
      // console.log('getUserDisplayName: No user data');
      return null;
    }
    
    // console.log('getUserDisplayName userData:', userData);
    
    // Try fullName first
    if (userData.fullName && typeof userData.fullName === 'string' && userData.fullName.trim()) {
      const name = userData.fullName.trim();
      // console.log('getUserDisplayName from fullName:', name);
      return name;
    }
    
    // Try name field
    if (userData.name && typeof userData.name === 'string' && userData.name.trim()) {
      const name = userData.name.trim();
      // console.log('getUserDisplayName from name:', name);
      return name;
    }
    
    // Try email as fallback (extract username part)
    if (userData.email && typeof userData.email === 'string' && userData.email.trim()) {
      const emailUsername = userData.email.split('@')[0];
      // console.log('getUserDisplayName from email:', emailUsername);
      return emailUsername;
    }
    
    // console.log('getUserDisplayName: No valid data found');
    return null;
  };

  // FIXED: Helper function to get user email
  const getUserEmail = () => {
    const userData = getActualUserData();
    if (!userData) {
      // console.log('getUserEmail: No user data');
      return null;
    }
    
    // console.log('getUserEmail userData:', userData);
    
    if (userData.email && typeof userData.email === 'string' && userData.email.trim()) {
      const email = userData.email.trim();
      // console.log('getUserEmail:', email);
      return email;
    }
    
    // console.log('getUserEmail: No valid email found');
    return null;
  };

  // FIXED: Check if user has valid data to display - PROPERLY RETURNS BOOLEAN
  const hasValidUserData = () => {
    // First check authentication
    if (!isAuthenticated || !user) {
      // console.log('hasValidUserData: Not authenticated or no user');
      return false;
    }
    
    const userData = getActualUserData();
    if (!userData) {
      // console.log('hasValidUserData: No actual user data found');
      return false;
    }
    
    // console.log('hasValidUserData checking userData:', userData);
    
    // Check if we have at least one valid display field
    const hasValidName = (userData.fullName && typeof userData.fullName === 'string' && userData.fullName.trim()) ||
                        (userData.name && typeof userData.name === 'string' && userData.name.trim());
    const hasValidEmail = userData.email && typeof userData.email === 'string' && userData.email.trim();
    
    const result = hasValidName || hasValidEmail;
    // console.log('hasValidUserData result:', result, { 
    //   isAuthenticated,
    //   hasUser: !!user,
    //   hasUserData: !!userData,
    //   hasValidName, 
    //   hasValidEmail,
    //   userDataKeys: userData ? Object.keys(userData) : [],
    //   fullUserKeys: user ? Object.keys(user) : []
    // });
    
    return result; // This will always return a boolean now
  };

  // Reorganized navigation items - grouped into mega menu
  const navItems = [
    { 
      name: 'Beranda', 
      link: isAuthenticated ? '/dashboard' : '/' 
    },
    { 
      name: 'Produk & Layanan', 
      link: '#',
      megaMenu: true,
      groups: [
        {
          title: 'Internet',
          items: [
            { name: 'Internet Broadband', link: '/internet/broadband' },
            { name: 'Internet Dedicated', link: '/internet/dedicated' },
            { name: 'Metro-E', link: '/internet/metro-e' },
          ]
        },
        {
          title: 'Layanan IT',
          items: [
            { name: 'IP Camera & CCTV', link: '/cctv' },
            { name: 'IP Phone PBX', link: '/layanan-it/ip-phone' },
            { name: 'IP TV', link: '/tv-iptv' },
            { name: 'IP Speaker System', link: '/layanan-it/ip-speaker' },
            { name: 'Tower Triangle', link: '/layanan-it/tower-triangle' },
            { name: 'Walkie Talkie/HT', link: '/layanan-it/walkie_talkie' },
            { name: 'Fiber Optic & LAN', link: '/layanan-it/fiber_optic' },
            { name: 'Perangkat Jaringan', link: '/layanan-it/network_device' },
            { name: 'Perangkat Komputer', link: '/layanan-it/computer_equipment' },
            { name: 'IT Support & Monitoring', link: '/layanan-it/it_support' },
            { name: 'Custom Software', link: '/layanan-it/custom_sofware' },
          ]
        },
        {
          title: 'Produk Lainnya',
          items: [
            { name: 'Internet Starlink', link: '/produk-lainnya/starlink' },
            { name: 'VPN', link: '/produk-lainnya/vpn' },
            { name: 'Traffic Management', link: '/produk-lainnya/traffic-management' },
            { name: 'Secure Firewall', link: '/produk-lainnya/secure-firewall' },
            { name: 'IP dan ASN', link: '/produk-lainnya/ip-asn' },
          ]
        }
      ]
    },
    { name: 'Berita', link: '/news' },
    { 
      name: 'Tentang Kami', 
      link: '/tentang',
      dropdown: [
        { name: 'Tentang Perusahaan', link: '/tentang' },
        { name: 'Gallery', link: '/gallery' },
        { name: 'FAQ', link: '/faq' },
        { name: 'Kebijakan Privasi', link: '/kebijakan-privasi' },
        { name: 'Ketentuan Layanan', link: '/ketentuan-layanan' },
        { name: 'Alur Transaksi', link: '/alur-transaksi' },
      ]
    },
    { name: 'Kontak', link: '/kontak' }
  ];

  // User dropdown menu items
  const userMenuItems = [
    { name: 'Dashboard', link: '/dashboard', icon: <HomeIcon className="h-5 w-5" /> },
    { name: 'Paket Saya', link: '/my-packages', icon: <CubeIcon className="h-5 w-5" /> },
    { name: 'Riwayat Pesanan', link: '/orders', icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
    { name: 'Tagihan', link: '/billing', icon: <CreditCardIcon className="h-5 w-5" /> },
    { name: 'Profil', link: '/profile', icon: <UserIcon className="h-5 w-5" /> },
    // { name: 'Pengaturan', link: '/settings', icon: <Cog6ToothIcon className="h-5 w-5" /> },
  ];

  // Loading state - simplified
  if (loading) {
    return (
      <nav className={`${
        isDarkMode 
          ? 'bg-gray-800 shadow-lg' 
          : 'bg-white shadow-lg border-b border-gray-200'
      } fixed top-0 z-[2000] w-full transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <img 
                src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png" 
                alt="Telnet Indonesia" 
                className="h-8 sm:h-10" 
              />
            </div>
            <div className="animate-pulse">
              <div className={`h-8 w-20 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded`}></div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const showUserData = hasValidUserData();
  const userInitial = showUserData ? getUserInitial() : null;
  const userDisplayName = showUserData ? getUserDisplayName() : null;
  const userEmail = showUserData ? getUserEmail() : null;

  // console.log(' Navbar render decision:', { 
  //   showUserData, 
  //   userInitial, 
  //   userDisplayName, 
  //   userEmail,
  //   isAuthenticated,
  //   hasUser: !!user,
  //   userType: typeof user,
  //   decision: showUserData ? 'SHOW USER DATA' : 'SHOW LOGIN/REGISTER'
  // });

  return (
    <>
      <nav className={`${
        isDarkMode 
          ? 'bg-gray-800 shadow-lg border-b border-gray-700' 
          : 'bg-white shadow-lg border-b border-gray-200'
      } fixed top-0 z-[2000] w-full transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* DESKTOP LAYOUT */}
          <div className="hidden lg:flex justify-between items-center h-16">
            
            {/* Desktop Logo (Left) - CHANGED TO FIXED */}
            <div className="flex-shrink-0" style={{ width: '150px' }}>
              <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex-shrink-0 fixed" style={{ left: '15px', top: '15px', zIndex: 2001 }}>
                <img 
                  src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png" 
                  alt="Telnet Indonesia" 
                  className="h-8 sm:h-10" 
                />
              </Link>
            </div>

            {/* Desktop Navigation (Center) */}
            <div className="flex items-center justify-center flex-1" style={{ marginLeft: '0px' }}>
              <div className="flex space-x-1">
                {navItems.map(item => (
                  <div key={item.name} className="relative group">
                    {item.megaMenu ? (
                      <button 
                        className={`${
                          isDarkMode 
                            ? 'text-gray-300 hover:text-[#1E9B4C]' 
                            : 'text-gray-700 hover:text-[#1E9B4C]'
                        } px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center`}
                      >
                        {item.name}
                        <svg className="ml-1 w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    ) : (
                      <Link 
                        to={item.link} 
                        className={`${
                          isDarkMode 
                            ? 'text-gray-300 hover:text-[#1E9B4C]' 
                            : 'text-gray-700 hover:text-[#1E9B4C]'
                        } px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                          location.pathname === item.link ? 'text-[#1E9B4C] font-semibold' : ''
                        }`}
                      >
                        {item.name}
                        {item.dropdown && (
                          <svg className="ml-1 w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </Link>
                    )}
                    
                    {/* Mega Menu Dropdown */}
                    {item.megaMenu && (
                      <div className={`absolute left-1/2 -translate-x-1/2 mt-1 w-[700px] ${
                        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                      } border rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50`}>
                        <div className="grid grid-cols-3 gap-0 p-6">
                          {item.groups.map((group, gIdx) => (
                            <div key={group.title} className={`${gIdx > 0 ? `border-l ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} pl-6` : ''}`}>
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-green-400' : 'text-[#1E9B4C]'}`}>
                                {group.title}
                              </h4>
                              <div className="space-y-1">
                                {group.items.map(subItem => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.link}
                                    className={`block px-3 py-1.5 text-sm rounded-md transition-colors ${
                                      isDarkMode 
                                        ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    } ${location.pathname === subItem.link ? 'text-[#1E9B4C] bg-green-50 dark:bg-green-900/20' : ''}`}
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Regular Dropdown Menu */}
                    {item.dropdown && !item.megaMenu && (
                      <div className={`absolute left-0 mt-1 w-56 ${
                        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                      } border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50`}>
                        <div className="py-2">
                          {item.dropdown.map(dropItem => (
                            <Link
                              key={dropItem.name}
                              to={dropItem.link}
                              className={`block px-4 py-2 text-sm transition-colors ${
                                isDarkMode 
                                  ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                              } ${location.pathname === dropItem.link ? 'text-[#1E9B4C] bg-green-50' : ''}`}
                            >
                              {dropItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop Actions (Right) */}
            <div className="flex items-center space-x-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode
                    ? 'text-yellow-400 hover:text-yellow-300 hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
                title={isDarkMode ? 'Mode Gelap' : 'Mode Terang'}
              >
                {isDarkMode ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* User Authentication Area */}
              {showUserData ? (
                /* Desktop User Dropdown Menu */
                <div className="relative group">
                  <button className={`flex items-center space-x-3 px-4 py-2 rounded-full transition-all duration-300 ${
                    isDarkMode 
                      ? 'text-white hover:bg-gray-700 bg-gray-800 border border-gray-600' 
                      : 'text-gray-700 hover:bg-gray-50 bg-white border border-gray-300 shadow-sm'
                  }`}>
                    {/* User Avatar */}
                    {userInitial && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1E9B4C] to-green-700 flex items-center justify-center text-white font-semibold text-sm">
                        {userInitial}
                      </div>
                    )}
                    
                    {/* User Name */}
                    {userDisplayName && (
                      <span className="font-medium text-sm max-w-32 truncate">
                        {userDisplayName}
                      </span>
                    )}
                    
                    {/* Dropdown Arrow */}
                    <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Desktop User Dropdown Menu */}
                  <div className={`absolute right-0 mt-2 w-72 ${
                    isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  } border rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50`}>
                    
                    {/* User Info Header */}
                    <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <div className="flex items-center space-x-3">
                        {userInitial && (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#1E9B4C] to-green-700 flex items-center justify-center text-white font-semibold">
                            {userInitial}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {userDisplayName && (
                            <p className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {userDisplayName}
                            </p>
                          )}
                          {userEmail && (
                            <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {userEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* User Menu Items */}
                    <div className="space-y-1">
                      {userMenuItems.map(item => (
                        <Link
                          key={item.name}
                          to={item.link}
                          className={`flex items-center space-x-3 px-4 py-3 text-base transition-all rounded-lg ${
                            isDarkMode 
                              ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                          } ${location.pathname === item.link ? 'text-[#1E9B4C] bg-green-50' : ''}`}
                        >
                          <span className="text-xl">{item.icon}</span>
                          <span>{item.name}</span>
                        </Link>
                      ))}
                    </div>

                    {/* Logout Button */}
                    <div className={`px-2 py-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <button
                        onClick={handleLogout}
                        className={`flex items-center space-x-3 w-full px-4 py-3 text-base transition-all rounded-lg ${
                          isDarkMode 
                            ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' 
                            : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                        }`}
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5" />
                        <span>Keluar</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop Login/Register Buttons */
                <div className="flex items-center space-x-2">
                  <Link 
                    to="/login" 
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      isDarkMode 
                        ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Masuk
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-[#1E9B4C] hover:bg-green-700 text-white px-6 py-2 rounded-full font-semibold transition-colors shadow-md hover:shadow-lg"
                  >
                    Daftar Sekarang
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* MOBILE LAYOUT - Tetap sama seperti sebelumnya */}
          <div className="lg:hidden flex justify-between items-center h-16">
            
            {/* Mobile Left Section: Hamburger Menu */}
            <div className="flex items-center">
              <button 
                onClick={toggleMenu}
                className={`p-2 rounded-md ${
                  isDarkMode 
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                } focus:outline-none transition-colors`}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>

            {/* Mobile Center Section: Logo */}
            <div className="flex items-center justify-center flex-1">
              <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center">
                <img 
                  src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png" 
                  alt="Telnet Indonesia" 
                  className="h-8" 
                />
              </Link>
            </div>

          <div className="flex items-center space-x-2">
            {/* Mobile User Avatar (If logged in and has valid data) */}
            {(() => {
              if (showUserData && userInitial) {
                return (
                  <button 
                    onClick={toggleUserDropdown}
                    className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1E9B4C] to-green-700 flex items-center justify-center text-white font-semibold text-sm"
                  >
                    {userInitial}
                  </button>
                );
              } else if (!showUserData) {
                return (
                  <div className="flex items-center space-x-1">
                    <Link 
                      to="/login" 
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        isDarkMode 
                          ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      Masuk
                    </Link>
                    <Link 
                      to="/register" 
                      className="bg-[#1E9B4C] hover:bg-green-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold transition-colors"
                    >
                      Daftar
                    </Link>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          </div>

          {/* Mobile User Dropdown (Show below navbar) */}
          {activeUserDropdown && showUserData && (
            <div className={`lg:hidden absolute left-4 right-4 mt-2 ${
              isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
            } border rounded-xl shadow-lg z-50`}>
              
              {/* User Info Header */}
              <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  {userInitial && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#1E9B4C] to-green-700 flex items-center justify-center text-white font-semibold">
                      {userInitial}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {userDisplayName && (
                      <p className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {userDisplayName}
                      </p>
                    )}
                    {userEmail && (
                      <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {userEmail}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* User Menu Items */}
              <div className="space-y-1 p-2">
                {userMenuItems.map(item => (
                  <Link
                    key={item.name}
                    to={item.link}
                    className={`flex items-center space-x-3 px-4 py-3 text-base transition-all rounded-lg ${
                      isDarkMode 
                        ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    } ${location.pathname === item.link ? 'text-[#1E9B4C] bg-green-50' : ''}`}
                    onClick={() => setActiveUserDropdown(false)}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span>{item.name}</span>
                  </Link>
                ))}

                {/* Mobile Logout Button */}
                <div className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} pt-2 mt-2`}>
                  <button
                    onClick={handleLogout}
                    className={`flex items-center space-x-3 w-full px-4 py-3 text-base transition-all rounded-lg ${
                      isDarkMode 
                        ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' 
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                    }`}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    <span>Keluar</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Full Screen Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={toggleMenu}
            />
            
            {/* Mobile Menu Container */}
            <div className={`fixed inset-y-0 left-0 w-4/5 max-w-sm ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            } transition-all duration-300 ease-in-out flex flex-col transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
              
              {/* Mobile Menu Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <Link 
                  to={isAuthenticated ? "/dashboard" : "/"} 
                  className="flex items-center"
                  onClick={toggleMenu}
                >
                  <img 
                    src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png" 
                    alt="Telnet Indonesia" 
                    className="h-8" 
                  />
                </Link>
                
                <button 
                  onClick={toggleMenu}
                  className={`p-2 rounded-full transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile Menu Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="px-4">

                  {/* Mobile Dark Mode Toggle */}
                  <div className="py-4 border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={toggleDarkMode}
                      className={`flex items-center space-x-3 w-full px-4 py-3 text-base transition-all rounded-lg ${
                        isDarkMode
                          ? 'text-yellow-400 hover:text-yellow-300 hover:bg-gray-800'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      {isDarkMode ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                      )}
                      <span>{isDarkMode ? 'Mode Gelap' : 'Mode Terang'}</span>
                    </button>
                  </div>

                  {/* Navigation Menu */}
                  <div className="space-y-2 py-4">
                    {navItems.map(item => (
                      <div key={item.name} className="space-y-1">
                        {(item.dropdown || item.megaMenu) ? (
                          <div>
                            <button
                              onClick={() => toggleMobileDropdown(item.name)}
                              className={`${
                                isDarkMode 
                                  ? 'text-gray-300 hover:text-[#1E9B4C] hover:bg-gray-800' 
                                  : 'text-gray-700 hover:text-[#1E9B4C] hover:bg-gray-50'
                              } flex items-center justify-between w-full px-4 py-3 rounded-lg text-base font-medium transition-all`}
                            >
                              <span>{item.name}</span>
                              <svg 
                                className={`w-5 h-5 transform transition-transform ${
                                  activeDropdown === item.name ? 'rotate-180' : ''
                                }`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Mobile Dropdown Content */}
                            {activeDropdown === item.name && item.megaMenu && (
                              <div className="ml-4 space-y-3 mt-2">
                                {item.groups.map(group => (
                                  <div key={group.title}>
                                    <h4 className={`text-xs font-bold uppercase tracking-wider px-4 mb-1 ${isDarkMode ? 'text-green-400' : 'text-[#1E9B4C]'}`}>
                                      {group.title}
                                    </h4>
                                    {group.items.map(subItem => (
                                      <Link
                                        key={subItem.name}
                                        to={subItem.link}
                                        className={`${
                                          isDarkMode 
                                            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' 
                                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                        } flex items-center px-4 py-2 rounded-md text-sm transition-colors ${
                                          location.pathname === subItem.link ? 'text-[#1E9B4C] bg-green-50' : ''
                                        }`}
                                        onClick={toggleMenu}
                                      >
                                        <span className="mr-2">•</span>
                                        {subItem.name}
                                      </Link>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                            {activeDropdown === item.name && item.dropdown && !item.megaMenu && (
                              <div className="ml-4 space-y-1 mt-2">
                                {item.dropdown.map(dropItem => (
                                  <Link
                                    key={dropItem.name}
                                    to={dropItem.link}
                                    className={`${
                                      isDarkMode 
                                        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' 
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    } flex items-center px-4 py-2 rounded-md text-sm transition-colors ${
                                      location.pathname === dropItem.link ? 'text-[#1E9B4C] bg-green-50' : ''
                                    }`}
                                    onClick={toggleMenu}
                                  >
                                    <span className="mr-2">•</span>
                                    {dropItem.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            to={item.link}
                            className={`${
                              isDarkMode 
                                ? 'text-gray-300 hover:text-[#1E9B4C] hover:bg-gray-800' 
                                : 'text-gray-700 hover:text-[#1E9B4C] hover:bg-gray-50'
                            } block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                              location.pathname === item.link ? 'text-[#1E9B4C] bg-green-50' : ''
                            }`}
                            onClick={toggleMenu}
                          >
                            {item.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile Menu Footer */}
              <div className={`px-4 py-4 border-t ${
                isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="text-center">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    © 2025 Telnet Indonesia
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Connecting Indonesia to the World
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;